import { useEffect, useState } from "react";
import { Check, Copy, Download } from "lucide-react";
import { MarkdownView } from "@/shared/components/recording/MarkdownView";
import type { Locale } from "@/shared/i18n/translations";
import { conversationAnalysisCopy } from "./conversationAnalysisCopy";

export function ConversationSummary({
  summary,
  sessionId,
  locale,
}: {
  summary: string;
  sessionId: string;
  locale: Locale;
}) {
  const copy = conversationAnalysisCopy(locale);
  const [copyState, setCopyState] = useState<"idle" | "copying" | "copied">(
    "idle",
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (copyState !== "copied") return;
    const timeout = window.setTimeout(() => setCopyState("idle"), 2000);
    return () => window.clearTimeout(timeout);
  }, [copyState]);

  const copySummary = async () => {
    setError(null);
    setCopyState("copying");
    try {
      await navigator.clipboard.writeText(summary);
      setCopyState("copied");
    } catch {
      setCopyState("idle");
      setError(copy.copyFailed);
    }
  };

  const downloadSummary = () => {
    setError(null);
    let url: string | undefined;
    const anchor = document.createElement("a");
    try {
      url = URL.createObjectURL(
        new Blob([summary], { type: "text/markdown;charset=utf-8" }),
      );
      anchor.href = url;
      anchor.download = `conversation-analysis-${sessionId}.md`;
      document.body.appendChild(anchor);
      anchor.click();
    } catch {
      setError(copy.downloadFailed);
    } finally {
      anchor.remove();
      // Let the browser start reading the file before releasing its URL.
      if (url) {
        const downloadUrl = url;
        window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
      }
    }
  };

  const copied = copyState === "copied";

  return (
    <section className="eq-session-surface">
      <div
        className="mb-5 flex flex-wrap items-center justify-end gap-2"
        role="group"
        aria-label={copy.analysis}
      >
        <button
          type="button"
          className="eq-button min-h-11 min-w-24"
          disabled={!summary.trim() || copyState === "copying"}
          onClick={() => void copySummary()}
        >
          {copied ? (
            <Check size={15} aria-hidden="true" />
          ) : (
            <Copy size={15} aria-hidden="true" />
          )}
          {copied ? copy.copied : copy.copy}
        </button>
        <button
          type="button"
          className="eq-button min-h-11"
          title={`${copy.download} (.md)`}
          disabled={!summary.trim()}
          onClick={downloadSummary}
        >
          <Download size={15} aria-hidden="true" />
          {copy.download}
        </button>
        <span className="sr-only" role="status">
          {copied ? copy.copied : ""}
        </span>
      </div>
      {error && (
        <p className="eq-session-error" role="alert">
          {error}
        </p>
      )}
      <MarkdownView markdown={summary} />
    </section>
  );
}
